'use client';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

interface ChatUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'user';
  isActive: boolean;
  lastLoginAt?: string;
  createdAt: string;
  authType?: 'PASSWORD' | 'SAML';
  avatarUrl?: string;
  totpEnabled?: boolean;
}

interface InviteUserData {
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'user';
  authType: 'PASSWORD' | 'SAML';
}

interface UpdateUserData {
  firstName?: string;
  lastName?: string;
  role?: 'admin' | 'user';
  isActive?: boolean;
}

export function useUsers(agentId: string) {
  const queryClient = useQueryClient();

  const {
    data: users = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<ChatUser[]>({
    queryKey: ['users', agentId],
    queryFn: async () => {
      const response = await fetch(`/api/chat/agents/${agentId}/users`);
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || `Failed to load users (${response.status})`);
      }
      
      return response.json();
    },
    // Cache for 2 minutes
    staleTime: 2 * 60 * 1000,
    // Keep in cache for 5 minutes
    gcTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      // Don't retry on auth errors
      if (error instanceof Error && error.message.includes('401')) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Mutation for inviting users
  const inviteUser = useMutation({
    mutationFn: async (data: InviteUserData) => {
      const response = await fetch(`/api/chat/agents/${agentId}/users/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to invite user');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch users
      queryClient.invalidateQueries({ queryKey: ['users', agentId] });
    },
  });

  // Mutation for updating users
  const updateUser = useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: UpdateUserData }) => {
      const response = await fetch(`/api/chat/agents/${agentId}/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update user');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch users
      queryClient.invalidateQueries({ queryKey: ['users', agentId] });
    },
  });

  // Mutation for deleting users
  const deleteUser = useMutation({
    mutationFn: async (userId: string) => {
      const response = await fetch(`/api/chat/agents/${agentId}/users/${userId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete user');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch users
      queryClient.invalidateQueries({ queryKey: ['users', agentId] });
    },
  });

  // Mutation for resetting user password
  const resetUserPassword = useMutation({
    mutationFn: async (userId: string) => {
      const response = await fetch(`/api/chat/agents/${agentId}/users/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reset password');
      }

      return response.json();
    },
  });

  // Mutation for uploading user avatar
  const uploadAvatar = useMutation({
    mutationFn: async ({ userId, file }: { userId: string; file: File }) => {
      const formData = new FormData();
      formData.append('avatar', file);

      const response = await fetch(`/api/chat/agents/${agentId}/users/${userId}/avatar`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload avatar');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch users
      queryClient.invalidateQueries({ queryKey: ['users', agentId] });
    },
  });

  return {
    users,
    isLoading,
    isError,
    error,
    refetch,
    inviteUser,
    updateUser,
    deleteUser,
    resetUserPassword,
    uploadAvatar,
  };
}